import { zodResolver } from '@hookform/resolvers/zod';
import { Ellipsis, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import z from 'zod';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/shadcn/alert-dialog';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/shadcn/form';
import { Input } from '@/shared/components/shadcn/input';

type Props = {
  onDelete: () => Promise<void>;
  onRename: (title: string) => Promise<void>;
  title: string;
};

const renameFormSchema = z.object({
  title: z.string().trim().min(1, 'Enter a report name.').max(200, 'Use 200 characters or fewer.'),
});

const deleteFormSchema = z.object({
  confirmation: z.string(),
});

type RenameFormValues = z.infer<typeof renameFormSchema>;
type DeleteFormValues = z.infer<typeof deleteFormSchema>;

export function CampaignAnalysisActionsMenu({ onDelete, onRename, title }: Props) {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button aria-label="Open report actions" size="icon" variant="ghost">
            <Ellipsis aria-hidden="true" className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={() => setIsRenameDialogOpen(true)}>
            <Pencil aria-hidden="true" className="size-4" />
            Rename report
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setIsDeleteDialogOpen(true)} variant="destructive">
            <Trash2 aria-hidden="true" className="size-4" />
            Delete report
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RenameCampaignAnalysisDialog
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        onRename={onRename}
        title={title}
      />
      <DeleteCampaignAnalysisDialog
        isOpen={isDeleteDialogOpen}
        onDelete={onDelete}
        onOpenChange={setIsDeleteDialogOpen}
        title={title}
      />
    </>
  );
}

function RenameCampaignAnalysisDialog({
  isOpen,
  onOpenChange,
  onRename,
  title,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onRename: (title: string) => Promise<void>;
  title: string;
}) {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const form = useForm<RenameFormValues>({
    defaultValues: { title },
    resolver: zodResolver(renameFormSchema),
  });
  const renameText = useWatch({ control: form.control, name: 'title' });
  const isSubmitting = form.formState.isSubmitting;
  const trimmedRenameText = renameText.trim();
  const canRename = trimmedRenameText.length > 0 && trimmedRenameText !== title && !isSubmitting;

  const closeDialog = () => {
    if (isSubmitting) return;
    setSubmissionError(null);
    form.reset({ title });
    onOpenChange(false);
  };

  const submitRename = async (values: RenameFormValues) => {
    if (values.title === title) return;

    setSubmissionError(null);
    try {
      await onRename(values.title);
      form.reset({ title: values.title });
      onOpenChange(false);
    } catch {
      setSubmissionError('The report could not be renamed. Please try again.');
    }
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          setSubmissionError(null);
          form.reset({ title });
          onOpenChange(true);
        } else {
          closeDialog();
        }
      }}
      open={isOpen}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Rename report</DialogTitle>
          <DialogDescription>Update the report name shown in this analysis.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            noValidate
            onSubmit={form.handleSubmit(submitRename)}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Report name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        field.onChange(event);
                        setSubmissionError(null);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submissionError ? (
              <p className="text-destructive text-sm" role="alert">
                {submissionError}
              </p>
            ) : null}
            <DialogFooter>
              <Button disabled={isSubmitting} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={!canRename} loading={isSubmitting} type="submit">
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCampaignAnalysisDialog({
  isOpen,
  onDelete,
  onOpenChange,
  title,
}: {
  isOpen: boolean;
  onDelete: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
  title: string;
}) {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const form = useForm<DeleteFormValues>({
    defaultValues: { confirmation: '' },
    resolver: zodResolver(
      deleteFormSchema.refine((values) => values.confirmation === title, {
        message: 'Enter the report title exactly as shown.',
        path: ['confirmation'],
      }),
    ),
  });
  const confirmationText = useWatch({
    control: form.control,
    name: 'confirmation',
  });
  const isSubmitting = form.formState.isSubmitting;
  const isDeleteConfirmed = confirmationText === title;

  const closeDialog = () => {
    if (isSubmitting) return;
    setSubmissionError(null);
    form.reset();
    onOpenChange(false);
  };

  const submitDelete = async () => {
    setSubmissionError(null);
    try {
      await onDelete();
      form.reset();
      onOpenChange(false);
    } catch {
      setSubmissionError('The report could not be deleted. Please try again.');
    }
  };

  return (
    <AlertDialog
      onOpenChange={(open) => {
        if (open) {
          setSubmissionError(null);
          form.reset();
          onOpenChange(true);
        } else {
          closeDialog();
        }
      }}
      open={isOpen}
    >
      <AlertDialogContent className="sm:max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to delete this report?</AlertDialogTitle>
          <AlertDialogDescription className="leading-6">
            Deleting will permanently remove this report from the campaign analysis history.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-5"
            noValidate
            onSubmit={form.handleSubmit(submitDelete)}
          >
            <div className="bg-muted/40 rounded-lg border p-4">
              <p className="text-muted-foreground text-sm font-medium">
                Type the report title to confirm
              </p>
              <p className="text-foreground mt-2 text-sm font-semibold">{title}</p>
            </div>
            <FormField
              control={form.control}
              name="confirmation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmation</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        field.onChange(event);
                        form.clearErrors('confirmation');
                        setSubmissionError(null);
                      }}
                      placeholder={`Type "${title}"`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {submissionError ? (
              <p className="text-destructive text-sm" role="alert">
                {submissionError}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting} type="button">
                Cancel
              </AlertDialogCancel>
              <Button
                disabled={!isDeleteConfirmed || isSubmitting}
                loading={isSubmitting}
                type="submit"
                variant="destructive"
              >
                Delete report
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
